import React from "react";
import Section from "./Section";

export default function HTML_Styles() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🎨 HTML Styles</h1>

      {/* 🔹 Introduction */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "1.5rem",
          marginBottom: "2rem",
        }}
      >
        <p className="tutorial-intro">
          HTML <strong>styles</strong> are used to control the appearance and
          layout of web pages. There are three main types of styling methods:
          <strong> Inline</strong>, <strong>Internal</strong>, and{" "}
          <strong>External</strong>.
        </p>
      </div>

      {/* 🔹 History Section */}
      <Section
        title="📜 Introduction & History of HTML Styles"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>HTML Styles History</title>
  </head>
  <body>
    <h1>HTML Style History</h1>
    <p>
      In early web design, developers used tags like <b>&lt;font&gt;</b> and
      attributes such as <b>bgcolor</b> to style web pages.
    </p>
    <p>
      CSS (Cascading Style Sheets) was later introduced to separate structure
      (HTML) from presentation (style), creating cleaner and more efficient code.
    </p>
  </body>
</html>`}
        description="In the early days of the web, HTML handled both content and styling. CSS was introduced to separate design from structure, making web development faster and cleaner."
      />

      {/* 🔹 Inline Styles Section */}
      <Section
        title="🎯 Inline Styles"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>Inline Styles Example</title>
  </head>
  <body>
    <h1 style="color: blue; text-align: center;">Welcome to My Website</h1>
    <p style="font-size: 18px; color: green;">
      This paragraph uses inline CSS styling applied directly to the element.
    </p>
  </body>
</html>`}
        description="Inline styles are applied directly to an element using the 'style' attribute. They are useful for small, one-time customizations."
      />

      {/* 🔹 Internal Styles Section */}
      <Section
        title="🏠 Internal Styles"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>Internal Styles Example</title>
    <style>
      body {
        background-color: #f0f0f0;
      }
      h1 {
        color: darkblue;
        text-align: center;
      }
      p {
        color: #333;
        font-size: 18px;
      }
    </style>
  </head>
  <body>
    <h1>Styled with Internal CSS</h1>
    <p>This page uses internal CSS inside the &lt;style&gt; tag.</p>
  </body>
</html>`}
        description="Internal CSS is defined within a <style> element inside the <head> section. It's good for single-page projects or testing purposes."
      />

      {/* 🔹 External Styles Section */}
      <Section
        title="🌍 External Styles"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>External Styles Example</title>
    <link rel="stylesheet" href="Style.css" />
  </head>
  <body>
    <h1>Styled with External CSS</h1>
    <p>This page links to an external CSS file named Style.css.</p>
  </body>
</html>`}
        description="External CSS is written in a separate .css file and linked using the <link> tag in the <head> section. It’s ideal for large or multi-page websites."
      />

      {/* 🔹 External CSS File Example */}
      <Section
        title="🗂️ Example: Style.css File"
        html={`/* Style.css */
body {
  background-color: #f9fafb;
  font-family: Arial, sans-serif;
  margin: 40px;
}

h1 {
  color: #1e40af;
  text-align: center;
  font-size: 36px;
}

p {
  color: #111827;
  font-size: 18px;
  text-align: center;
}`}
        description="This is an example of a Style.css file linked to the HTML page. It defines the styles for the body, heading, and paragraph globally across all pages using the same stylesheet."
      />

      {/* 🔹 Comparison Table */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📊 Comparison Between Style Types</h2>
        <table className="tutorial-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Where Defined</th>
              <th>Usage</th>
              <th>Best For</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Inline</td>
              <td>Inside element (style attribute)</td>
              <td>Quick, small changes</td>
              <td>Temporary styling</td>
            </tr>
            <tr>
              <td>Internal</td>
              <td>In &lt;style&gt; inside &lt;head&gt;</td>
              <td>Page-specific styles</td>
              <td>Single-page projects</td>
            </tr>
            <tr>
              <td>External</td>
              <td>Separate Style.css file</td>
              <td>Reusable, organized styling</td>
              <td>Large websites</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 🔹 Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #d1fae5, #a7f3d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>✅ Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>Use external CSS for consistent, reusable design.</li>
          <li>Keep HTML (structure) and CSS (style) separate.</li>
          <li>Avoid inline styles for large-scale styling.</li>
          <li>Maintain one central stylesheet for easier updates.</li>
        </ul>
      </div>

      {/* 🔹 Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary</h2>
        <ul className="tutorial-list-points">
          <li>
            HTML supports three styling methods: Inline, Internal, and External.
          </li>
          <li>
            External CSS (Style.css) is the most efficient for maintaining large
            websites.
          </li>
          <li>
            CSS separates design from structure, improving reusability and
            clarity.
          </li>
        </ul>
      </div>
    </div>
  );
}
